import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { KONTO_LOESCHEN } from "@/constants/testIds/landing";
import { useSiteContent } from "@/lib/siteContent";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function KontoLoeschen() {
  const { content } = useSiteContent();
  const contactEmail = content.contact_email || "christianberres1@gmail.com";
  const [form, setForm] = useState({
    email: "",
    account_id: "",
    reason: "",
    confirm: false,
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.email || !form.email.includes("@")) {
      setError("Bitte gib eine gültige E-Mail-Adresse ein.");
      return;
    }
    if (!form.confirm) {
      setError("Bitte bestätige, dass du die Löschung deines Kontos beantragst.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/deletion-request`, {
        email: form.email.trim(),
        account_id: form.account_id.trim() || null,
        reason: form.reason.trim() || null,
        confirm: form.confirm,
      });
      setSuccess(true);
      toast.success("Löschanfrage erhalten – wir melden uns per E-Mail.");
      setForm({ email: "", account_id: "", reason: "", confirm: false });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Anfrage fehlgeschlagen. Bitte versuche es erneut.";
      setError(detail);
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid={KONTO_LOESCHEN.page}
      className="min-h-screen bg-[#FAFAFA] text-[#1A1A1A]"
    >
      <Header onCtaClick={() => {}} />

      <main className="pt-32 pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            data-testid={KONTO_LOESCHEN.backToHome}
            className="inline-flex items-center gap-2 text-sm text-[#666] hover:text-[#6B8E23] transition-colors mb-8"
          >
            <ArrowLeft size={16} /> Zurück zur Startseite
          </Link>

          <div className="flex items-center gap-3 mb-3">
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-[#FEF3F2] text-[#B42318]">
              <Trash2 size={22} />
            </span>
            <h1 className="font-[Outfit] text-3xl sm:text-4xl font-bold tracking-tight">
              Konto & Daten löschen
            </h1>
          </div>
          <p className="text-[#666] max-w-2xl">
            Du möchtest dein Waskowi-Konto löschen lassen? Sende uns über das
            Formular eine Löschanfrage. Wir bestätigen den Eingang per E-Mail
            und löschen deine Daten innerhalb von <strong>30 Tagen</strong>.
          </p>

          <section className="mt-10 bg-white rounded-3xl border border-[#EAEAEA] p-6 sm:p-8">
            <h2 className="font-[Outfit] text-xl font-semibold mb-3">
              Welche Daten werden gelöscht?
            </h2>
            <ul className="text-sm text-[#555] space-y-2 list-disc pl-5">
              <li>Deine E-Mail-Adresse aus der Testerliste</li>
              <li>Alle in der App gespeicherten Einkaufslisten, Rezepte und Pläne</li>
              <li>Profilinformationen (z.&nbsp;B. Name, Geräteinformationen)</li>
              <li>Logdaten, sofern keine gesetzlichen Aufbewahrungspflichten bestehen</li>
            </ul>
          </section>

          <section className="mt-8 bg-white rounded-3xl border border-[#EAEAEA] p-6 sm:p-8">
            <h2 className="font-[Outfit] text-xl font-semibold mb-5">
              Löschanfrage stellen
            </h2>

            {success ? (
              <div
                data-testid={KONTO_LOESCHEN.successMessage}
                className="flex items-start gap-3 text-sm text-[#3A7D1E] bg-[#E8EFE1] border border-[#C9DCB7] rounded-2xl px-4 py-4"
              >
                <CheckCircle2 size={20} className="mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold mb-1">Anfrage erhalten</p>
                  <p>
                    Wir haben deine Löschanfrage empfangen. Du erhältst innerhalb
                    der nächsten Werktage eine Bestätigung per E-Mail. Die
                    Löschung erfolgt spätestens innerhalb von 30 Tagen.
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                    E-Mail-Adresse <span className="text-[#B42318]">*</span>
                  </label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, email: e.target.value }))
                    }
                    data-testid={KONTO_LOESCHEN.emailInput}
                    placeholder="die E-Mail, mit der du registriert bist"
                    className="rounded-2xl border border-[#E5E5E5] bg-white px-4 py-3 w-full focus:ring-2 focus:ring-[#6B8E23] focus:border-transparent outline-none transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                    Konto- / Geräte-ID (optional)
                  </label>
                  <input
                    type="text"
                    value={form.account_id}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, account_id: e.target.value }))
                    }
                    data-testid={KONTO_LOESCHEN.accountIdInput}
                    placeholder="z.B. interne Konto-ID, falls bekannt"
                    className="rounded-2xl border border-[#E5E5E5] bg-white px-4 py-3 w-full focus:ring-2 focus:ring-[#6B8E23] focus:border-transparent outline-none transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                    Grund für die Löschung (optional)
                  </label>
                  <textarea
                    rows={4}
                    value={form.reason}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, reason: e.target.value }))
                    }
                    data-testid={KONTO_LOESCHEN.reasonInput}
                    placeholder="Hilft uns, die App zu verbessern – ist aber nicht erforderlich."
                    className="rounded-2xl border border-[#E5E5E5] bg-white px-4 py-3 w-full focus:ring-2 focus:ring-[#6B8E23] focus:border-transparent outline-none transition-all resize-none"
                  />
                </div>

                <label className="flex items-start gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={form.confirm}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, confirm: e.target.checked }))
                    }
                    data-testid={KONTO_LOESCHEN.confirmCheckbox}
                    className="mt-1 w-4 h-4 rounded border-[#E5E5E5] accent-[#6B8E23]"
                  />
                  <span className="text-sm text-[#555]">
                    Ich beantrage hiermit unwiderruflich die Löschung meines
                    Waskowi-Kontos und aller damit verknüpften Daten.
                  </span>
                </label>

                {error && (
                  <div
                    data-testid={KONTO_LOESCHEN.errorMessage}
                    className="text-sm text-[#B42318] bg-[#FEF3F2] border border-[#FECDCA] rounded-2xl px-4 py-3"
                  >
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  data-testid={KONTO_LOESCHEN.submitButton}
                  className="rounded-full px-7 py-3 font-semibold bg-[#1A1A1A] text-white hover:bg-[#B42318] transition-all duration-200 hover:-translate-y-0.5 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  {loading ? "Sende…" : "Löschanfrage absenden"}
                </button>

                <p className="text-xs text-[#888] pt-2">
                  Alternativ kannst du eine Löschung auch per E-Mail an{" "}
                  <a
                    href={`mailto:${contactEmail}`}
                    className="underline underline-offset-2 hover:text-[#6B8E23]"
                  >
                    {contactEmail}
                  </a>{" "}
                  beantragen.
                </p>
              </form>
            )}
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
